import { Box, Typography } from "@mui/material";
import { ResponsiveHeatMapCanvas } from "@nivo/heatmap";
import { useContext, useEffect, useRef, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import LoadingIcon from "../../../../components/LoadingIcon";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import HeatmapTooltip from "../HeatmapTooltip";
import { getCoverageColor, getRiskColor } from "../utils";
import TwoLineYAxisLabel from "./TwoLineYAxisLabel";

// Custom wrapper for HeatMap that handles two-line labels
// showRisk is a boolean that determines if the heatmap should show risk or coverage for V2
const CustomHeatMap = ({
  data,
  colors,
  onCellClick,
  showRisk,
  isLoading,
  customLabels = null,
}) => {
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(650);
  const { trackEvent } = useAPI();
  const { heatmapOpacityDisabled } = useContext(CustomerFeatureFlagsContext);

  useEffect(() => {
    if (containerRef.current) {
      const height = containerRef.current.clientHeight;
      setContainerHeight(height - 50); // Subtract padding
    }
  }, []);

  // Convert to Nivo-compatible format
  const nivoData = data?.map((row) => ({
    id: row.id,
    data: row.data.map((item) => ({
      x: item.x,
      y: item.y.value, // Extract just the value for Nivo
    })),
  }));

  // Keep the original data for onClick handling
  const handleClick = (cell) => {
    // Find the original cell data
    const originalRow = data.find((row) => row.id === cell.serieId);
    const originalCell = originalRow?.data.find(
      (item) => item.x === cell.data.x
    );

    if (originalCell) {
      onCellClick({
        ...cell,
        data: {
          ...cell.data,
          y: originalCell.y, // Replace with original y (with value and practice)
        },
      });
    }
    trackEvent("User opened a practice dialog in the heatmap tile: " + cell.id);
  };

  // Function to get custom labels for cells
  const getCellLabel = (cell) => {
    if (!customLabels) return null;

    // Find the original cell data to get custom label
    const originalRow = data.find((row) => row.id === cell.serieId);
    const originalCell = originalRow?.data.find(
      (item) => item.x === cell.data.x
    );

    let customLabel = "";

    // Check if there's a custom label in the cell data
    if (originalCell?.y?.customLabel) {
      customLabel = originalCell.y.customLabel;
    }

    // If customLabels is a function, call it with cell data
    if (typeof customLabels === "function") {
      customLabel = customLabels(originalCell?.y || {});
    }

    // Get the cell value for arrow determination
    let numericValue = null;
    if (typeof customLabel === "string") {
      // Remove percent sign and whitespace, then try to parse as float
      const cleaned = customLabel.replace("%", "").trim();
      if (!isNaN(cleaned) && cleaned !== "") {
        numericValue = parseFloat(cleaned);
      }
    } else if (typeof customLabel === "number") {
      numericValue = customLabel;
    }
    const arrow = numericValue > 0 ? "↑" : numericValue < 0 ? "↓" : "";

    // Convert to string and check if it's a trend label (contains %)
    if (
      customLabel &&
      typeof customLabel === "string" &&
      customLabel.includes("%")
    ) {
      return arrow + customLabel;
    }

    // If it's a number (coverage), add arrow and % sign
    if (customLabel && !isNaN(customLabel)) {
      return arrow + customLabel + "%";
    }

    // If empty or no custom label, return just the arrow with the cell value
    if (
      customLabel === "" ||
      customLabel === null ||
      customLabel === undefined
    ) {
      return "";
    }

    // Return with arrow for other cases (convert to string if needed)
    return arrow + String(customLabel);
  };

  return (
    <Box position="relative" height="100%" width="100%" ref={containerRef}>
      {/* Custom Y-axis labels container */}
      <Box
        position="absolute"
        left={4}
        top={30}
        bottom={20}
        width="160px"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height={containerHeight}
        paddingY="8px"
      >
        {data.map((row, index) => {
          // Calculate y position based on container height
          const totalRows = data.length;

          return (
            <Box
              key={row.id}
              position="absolute"
              top={`${(index / totalRows) * 100}%`}
              height={`${100 / totalRows}%`}
              display="flex"
              alignItems="center"
              width="100%"
              paddingLeft={1}
              lineHeight="20px"
            >
              <TwoLineYAxisLabel y={0} label={row.id} colors={colors} />
            </Box>
          );
        })}
      </Box>

      {isLoading ? (
        <LoadingIcon />
      ) : (
        <>
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "100%",
              cursor: "pointer",
            }}
          >
            <ResponsiveHeatMapCanvas
              data={nivoData}
              animate={false}
              pixelRatio={1.5}
              enableLabels={false}
              label={(cell) => getCellLabel(cell)}
              xOuterPadding={0.05}
              margin={{ top: 50, right: 0, bottom: 20, left: 150 }}
              xInnerPadding={0.01}
              yInnerPadding={0.03}
              inactiveOpacity={customLabels || heatmapOpacityDisabled ? 1 : 0.4}
              axisLeft={null} // Disable the built-in left axis
              axisTop={{
                tickSize: 0,
                tickPadding: 24,
                tickRotation: 0,
                truncateTickAt: 100,
              }}
              theme={{
                axis: {
                  ticks: {
                    text: {
                      fontSize: 12,
                      fill: colors.text_secondary,
                      fontFamily: "poppins",
                    },
                    line: {
                      stroke: colors.grey_300,
                      strokeWidth: 1,
                    },
                  },
                },
                tooltip: {
                  wrapper: {
                    background: colors.background_dialog,
                    color: colors.text_secondary,
                    fontSize: 14,
                    fontFamily: "Poppins",
                    boxShadow: "0px 0px 2px 0px " + colors.grey_48percent,
                    borderRadius: "10px",
                  },
                },
                labels: {
                  text: {
                    fontSize: 12,
                    fill: colors.text_primary,
                    fontFamily: "poppins",
                    fontWeight: "bold",
                  },
                },
              }}
              colors={(cell) => {
                // Find the original severity value from the original data
                const originalRow = data.find((row) => row.id === cell.serieId);
                const originalCell = originalRow?.data.find(
                  (item) => item.x === cell.data.x
                );
                const value = originalCell?.y.value || 0;
                const opacityOtherCell =
                  originalCell?.y.opacityOtherCell && customLabels;

                return showRisk
                  ? getRiskColor(value, colors, opacityOtherCell)
                  : getCoverageColor(value, colors, opacityOtherCell);
              }}
              tooltip={HeatmapTooltip}
              onClick={customLabels ? null : handleClick}
            />
          </div>

          {customLabels && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: "150px", // Match heatmap left margin
                right: 0,
                bottom: 0,
                pointerEvents: "none", // Allow clicks to pass through to heatmap
                zIndex: 10,
              }}
            >
              {data?.map((row, rowIndex) => {
                const totalRows = data.length;
                const totalCols = row.data.length;

                // Calculate cell dimensions
                const heatmapWidth = 100; // Total width minus left margin
                const heatmapHeight = containerHeight - 10; // Total height minus margins

                const cellWidth = heatmapWidth / totalCols;
                const cellHeight = heatmapHeight / totalRows;

                return row.data.map((item, colIndex) => {
                  const customLabel = getCellLabel({
                    serieId: row.id,
                    data: { x: item.x },
                  });

                  if (!customLabel) return null;

                  // Calculate position for each badge
                  const leftPosition = colIndex * cellWidth + cellWidth / 2 + 5;
                  const topPosition =
                    rowIndex * cellHeight + cellHeight / 2 + 10;

                  return (
                    <Box
                      key={`${row.id}-${item.x}`}
                      sx={{
                        position: "absolute",
                        left: `${leftPosition}%`,
                        top: `${topPosition}px`,
                        transform: "translateX(-50%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {/* Background badge */}
                      <Box
                        sx={{
                          backgroundColor: colors.white_50percent,

                          borderRadius: "8px",
                          padding: "2px 8px",
                          minWidth: "40px",
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.action_active,
                          }}
                        >
                          {customLabel}
                        </Typography>
                      </Box>
                    </Box>
                  );
                });
              })}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CustomHeatMap;
